import { Offer, formatDealType } from 'app/console-api';
import {
  BodyText,
  Button,
  FlexColumn,
  FlexRow,
  formatDate,
  formatNumber,
  SvgIcon
} from '@make-software/cspr-design';
import React from 'react';
import styled from 'styled-components';
import { generatePath, useNavigate } from 'react-router-dom';
import { APPS_OFFERS_EDIT_PATH } from 'app/route/paths';
import DetailsTableRow from 'app/components/management/common/details-table-row/details-table-row';
import DetailsNameTableData from 'app/components/management/common/details-name-table-data/details-name-table-data';
import { BoldText, StyledStatusElement } from 'app/components/common/table/styled-elements';
import { StyledCaption } from 'app/components/management/apps/create/apps-create-step-1';
import DetailsValueTableData from 'app/components/management/common/details-value-table-data/details-value-table-data';
import CollapsibleContentPreview from 'app/components/management/common/content-view/collapse-content-preview';
import {DealTradeChip} from 'app/components/common/deal-trade-type/deal-trade-chip';
import { PluginChip } from 'app/components/management/common/plugins/plugin-chip/plugin-chip';

type OfferDetailsProps = {
  offer: Offer;
};

const StyledEditButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    margin: '40px 20px 20px 20px',
    maxWidth: 200
  })
);

export const OfferDetailsData = ({ offer }: OfferDetailsProps) => {
  const navigate = useNavigate();
  const edit = () => {
    navigate(generatePath(APPS_OFFERS_EDIT_PATH, { appID: offer.appID, id: offer.id }));
  };

  return (
    <FlexColumn>
      <StyledCaption margin={'24px 0 32px 17px'}>
        <BodyText size={1} scale={'lg'}>
          Details
        </BodyText>
      </StyledCaption>
      <DetailsTableRow>
        <DetailsNameTableData>ID</DetailsNameTableData>
        <DetailsValueTableData>{offer.id}</DetailsValueTableData>
      </DetailsTableRow>
      {offer.pluginID > 0 && (
        <DetailsTableRow>
          <DetailsNameTableData>Type</DetailsNameTableData>
          <DetailsValueTableData>
            <PluginChip id={offer.pluginID}></PluginChip>
          </DetailsValueTableData>
        </DetailsTableRow>
      )}
      <DetailsTableRow>
        <DetailsNameTableData>Status</DetailsNameTableData>
        <DetailsValueTableData>
          <BoldText size={4} monotype>
            {offer.isActive ? (
              <StyledStatusElement name={'Active'} isActive={true} />
            ) : (
              <StyledStatusElement name={'Inactive'} isActive={false} />
            )}
          </BoldText>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>App</DetailsNameTableData>
        <DetailsValueTableData>{offer.appID}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Name</DetailsNameTableData>
        <DetailsValueTableData>{offer.name}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Description</DetailsNameTableData>
        <DetailsValueTableData>{offer.description}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Deal Type</DetailsNameTableData>
        <DetailsValueTableData>
          <FlexRow gap={8}>
            <DealTradeChip type={offer.tradeType} />
          </FlexRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Custom metadata</DetailsNameTableData>
        <DetailsValueTableData>
          <CollapsibleContentPreview json={offer.customMetadata} />
        </DetailsValueTableData>
      </DetailsTableRow>
      {offer.pluginMetadata && (
        <DetailsTableRow>
          <DetailsNameTableData>Plugin metadata</DetailsNameTableData>
          <DetailsValueTableData>
            <CollapsibleContentPreview json={offer.pluginMetadata} />
          </DetailsValueTableData>
        </DetailsTableRow>
      )}
      <DetailsTableRow>
        <DetailsNameTableData>Daily min quota</DetailsNameTableData>
        <DetailsValueTableData>{formatNumber(offer.minQuota)}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Publish to catalog</DetailsNameTableData>
        <DetailsValueTableData>{offer.isPublic ? 'Yes' : 'No'}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Show statistics</DetailsNameTableData>
        <DetailsValueTableData>{offer.isPublicStats ? 'Yes' : 'No'}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Created At</DetailsNameTableData>
        <DetailsValueTableData>
          {formatDate(offer.createdAt.toLocaleString())}
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Updated At</DetailsNameTableData>
        <DetailsValueTableData>
          {formatDate(offer.createdAt.toLocaleString())}
        </DetailsValueTableData>
      </DetailsTableRow>
      <StyledEditButton onClick={edit}>Edit</StyledEditButton>
    </FlexColumn>
  );
};
